import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight, CheckCircle, FileText, Users, Target } from 'lucide-react';

export const HowWeWorkPage = () => {
  return (
    <div data-testid="how-we-work-page">
      {/* Hero Section */}
      <section className="hero-section py-16 md:py-20">
        <div className="container-law">
          <div className="breadcrumb text-white/60 mb-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span>How We Work</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white mb-4">How We Work</h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Inside counsel capability through an outside counsel model. We embed deeply in your business to deliver consistent, efficient legal support.
          </p>
        </div>
      </section>

      {/* Core Approach */}
      <section className="py-16 md:py-20">
        <div className="container-law">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="font-heading text-3xl text-navy mb-6">We Invest in Understanding Your Business</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Unlike traditional outside firms that approach each agreement as a one-off legal exercise, we invest upfront to understand your products, software, clinical or research workflows, and regulatory landscape.
              </p>
              <p className="text-gray-600 leading-relaxed">
                That understanding allows us to create baseline contract positions tailored to how your business actually operates—across SaaS, professional services, data licensing, clinical trials, real-world evidence, and analytics.
              </p>
            </div>
            <div className="bg-gray-50 border-l-4 border-green p-8">
              <h3 className="font-heading text-xl text-navy mb-4">The Contract Playbook</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                We work with you to define your risk tolerances and translate them into a clear contract negotiation playbook. That playbook standardizes positions on recurring issues:
              </p>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green" strokeWidth={1.5} />
                  IP ownership and licensing
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green" strokeWidth={1.5} />
                  Data rights and security
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green" strokeWidth={1.5} />
                  Confidentiality and audit rights
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green" strokeWidth={1.5} />
                  Indemnities and liability allocation
                </li>
              </ul>
            </div>
          </div>

          <h2 className="font-heading text-3xl text-navy mb-8">The Result</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="border border-gray-200 p-8">
              <div className="w-12 h-12 bg-green/10 flex items-center justify-center mb-6">
                <FileText className="w-6 h-6 text-green" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg text-navy mb-3">Fewer Escalations</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Standardized positions mean fewer decisions bouncing back to your team. We handle routine issues within established parameters.
              </p>
            </div>
            <div className="border border-gray-200 p-8">
              <div className="w-12 h-12 bg-green/10 flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-green" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg text-navy mb-3">Faster Review Cycles</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Because we carry business context into every negotiation, we move quickly without requiring repeated explanations of your requirements.
              </p>
            </div>
            <div className="border border-gray-200 p-8">
              <div className="w-12 h-12 bg-green/10 flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-green" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg text-navy mb-3">Consistent Outcomes</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Your deals close with consistent terms that reflect your actual risk tolerances—not whatever the last negotiator happened to accept.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Engagement Model */}
      <section className="py-16 bg-gray-50">
        <div className="container-law">
          <h2 className="font-heading text-3xl text-navy mb-8">Engagement Models</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-white border-l-4 border-green p-8">
              <h3 className="font-heading text-xl text-navy mb-3">Transaction Support</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Focused support for specific deals or agreements. We draft, review, and negotiate—then step back until you need us again.
              </p>
            </div>
            <div className="bg-white border-l-4 border-gray-300 p-8">
              <h3 className="font-heading text-xl text-navy mb-3">Ongoing Advisory</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Regular commercial and technology support for companies with steady transaction flow. Predictable availability, consistent counsel.
              </p>
            </div>
            <div className="bg-white border-l-4 border-gray-300 p-8">
              <h3 className="font-heading text-xl text-navy mb-3">Fractional General Counsel</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Comprehensive commercial legal support for companies that need senior counsel without the overhead of a full-time hire.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16">
        <div className="container-law">
          <h2 className="font-heading text-3xl text-navy mb-8">Getting Started</h2>
          <p className="text-gray-600 mb-12 max-w-2xl">
            Every engagement begins with a conversation about your business, your products, and your commercial environment.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 bg-white border border-gray-200">
            <div className="p-6 border-b md:border-b-0 md:border-r border-gray-200">
              <span className="text-green text-sm font-medium">Step 01</span>
              <h3 className="font-heading text-lg text-navy mt-3 mb-2">Discovery</h3>
              <p className="text-gray-600 text-sm">We learn your products, workflows, and regulatory landscape.</p>
            </div>
            <div className="p-6 border-b md:border-b-0 md:border-r border-gray-200">
              <span className="text-green text-sm font-medium">Step 02</span>
              <h3 className="font-heading text-lg text-navy mt-3 mb-2">Playbook Development</h3>
              <p className="text-gray-600 text-sm">We define your risk tolerances and create standardized contract positions.</p>
            </div>
            <div className="p-6 border-b md:border-b-0 md:border-r border-gray-200">
              <span className="text-green text-sm font-medium">Step 03</span>
              <h3 className="font-heading text-lg text-navy mt-3 mb-2">Execution</h3>
              <p className="text-gray-600 text-sm">We handle your transactions with the context to move efficiently.</p>
            </div>
            <div className="p-6">
              <span className="text-green text-sm font-medium">Step 04</span>
              <h3 className="font-heading text-lg text-navy mt-3 mb-2">Refinement</h3>
              <p className="text-gray-600 text-sm">We continuously improve positions based on deal outcomes and market changes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-50">
        <div className="container-law">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl text-navy mb-2">Ready to discuss your matter?</h2>
              <p className="text-gray-600">In-house depth without the overhead—and without the learning curve of traditional outside counsel.</p>
            </div>
            <Link to="/contact" className="btn-navy whitespace-nowrap" data-testid="how-we-work-cta-btn">
              Start a Conversation <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
